
import PromptCard, { PromptCardProps } from "./PromptCard";

interface PromptGridProps {
  prompts: PromptCardProps["prompt"][];
  columns?: 1 | 2 | 3;
  emptyMessage?: string;
  className?: string;
}

const PromptGrid = ({ 
  prompts, 
  columns = 3, 
  emptyMessage = "No prompts available.",
  className = ""
}: PromptGridProps) => {
  const gridClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  };

  if (!prompts) {
    return (
      <div className="text-center py-12 border border-dashed border-muted-foreground/20 rounded-lg">
        <p className="text-muted-foreground">Unable to load prompts. Please try again later.</p>
      </div>
    );
  }
  
  if (prompts.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-muted-foreground/20 rounded-lg">
        <p className="text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  // Filter out any potentially invalid prompts before rendering
  const validPrompts = prompts.filter(prompt => {
    if (!prompt || typeof prompt !== 'object' || !prompt.id || !prompt.title) {
      console.warn("Found invalid prompt in PromptGrid:", prompt);
      return false;
    }
    
    // Format category if it's a JSON string or object
    if (prompt.category !== null && prompt.category !== undefined) {
      if (typeof prompt.category === 'string' && prompt.category.startsWith('{')) {
        try {
          const categoryObj = JSON.parse(prompt.category);
          prompt.category = categoryObj.name || 'Uncategorized';
        } catch (error) {
          // Keep the original if parsing fails
          console.warn("Failed to parse category JSON:", error);
        }
      } else if (typeof prompt.category === 'object' && prompt.category !== null) {
        // Check if the object has a name property before accessing it
        // Using type assertion to help TypeScript understand the structure
        const categoryObj = prompt.category as { name?: string };
        if ('name' in categoryObj) {
          prompt.category = categoryObj.name || 'Uncategorized';
        } else {
          prompt.category = 'Uncategorized';
        }
      }
    } else {
      // Set a default value if category is null or undefined
      prompt.category = 'Uncategorized';
    }
    
    return true;
  });

  if (validPrompts.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-muted-foreground/20 rounded-lg">
        <p className="text-muted-foreground">Unable to display prompts due to data issues.</p>
      </div>
    );
  }

  return (
    <div className={`grid ${gridClasses[columns]} gap-4 ${className}`}>
      {validPrompts.map((prompt) => (
        <PromptCard key={prompt.id} prompt={prompt} />
      ))}
    </div>
  );
};

export default PromptGrid;
